import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient } from "@/common/apiClient";
import { divisions } from "@/shared/data/division";

interface Question {
  id: string;
  text: string;
  order: number;
}

interface QuestionList {
  _id: string;
  title: string;
  description: string;
  questions: Question[];
}

interface SelectedQuestionList {
  questionListId: string;
  title: string;
  order: number;
}

export const CreateKPIForm = () => {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [division, setDivision] = useState("");
  const [questionLists, setQuestionLists] = useState<QuestionList[]>([]);
  const [selectedQuestionLists, setSelectedQuestionLists] = useState<
    SelectedQuestionList[]
  >([]);
  const [isLoadingLists, setIsLoadingLists] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuestionLists = async () => {
      try {
        const response = await apiClient.get("/api/kpi-question-list");
        if (response.success) {
          setQuestionLists(response.data || []);
        } else {
          toast.error("Failed to load question lists");
        }
      } catch (error) {
        toast.error("Failed to load question lists: " + (error as any).message);
      } finally {
        setIsLoadingLists(false);
      }
    };

    fetchQuestionLists();
  }, []);

  const handleQuestionListToggle = (questionListId: string) => {
    const list = questionLists.find((ql) => ql._id === questionListId);
    if (!list) return;

    const isSelected = selectedQuestionLists.some(
      (sql) => sql.questionListId === questionListId
    );

    if (isSelected) {
      // Remove from selection
      setSelectedQuestionLists((prev) =>
        prev.filter((sql) => sql.questionListId !== questionListId)
      );
    } else {
      // Add to selection
      setSelectedQuestionLists((prev) => [
        ...prev,
        {
          questionListId: list._id,
          title: list.title,
          order: prev.length + 1,
        },
      ]);
    }
  };

  const moveQuestionListUp = (index: number) => {
    if (index === 0) return;

    const newLists = [...selectedQuestionLists];
    [newLists[index - 1], newLists[index]] = [
      newLists[index],
      newLists[index - 1],
    ];

    // Update order
    newLists.forEach((ql, i) => {
      ql.order = i + 1;
    });

    setSelectedQuestionLists(newLists);
  };

  const moveQuestionListDown = (index: number) => {
    if (index === selectedQuestionLists.length - 1) return;

    const newLists = [...selectedQuestionLists];
    [newLists[index], newLists[index + 1]] = [
      newLists[index + 1],
      newLists[index],
    ];

    // Update order
    newLists.forEach((ql, i) => {
      ql.order = i + 1;
    });

    setSelectedQuestionLists(newLists);
  };

  const getPreviewQuestions = () => {
    const allQuestions: Array<{ text: string; questionListTitle: string }> = [];

    selectedQuestionLists.forEach((selectedList) => {
      const list = questionLists.find(
        (ql) => ql._id === selectedList.questionListId
      );
      if (list && list.questions) {
        list.questions.forEach((q) => {
          allQuestions.push({
            text: q.text,
            questionListTitle: list.title,
          });
        });
      }
    });

    return allQuestions;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedQuestionLists.length === 0) {
        toast.error("Please select at least one question list");
        return;
      }

      const response = await apiClient.post("/api/kpi-form", {
        title,
        description,
        division,
        questionLists: selectedQuestionLists.map((sql) => ({
          questionListId: sql.questionListId,
          order: sql.order,
        })),
      });

      if (response.success) {
        toast.success("KPI Form created successfully!");
        navigate("/view-all-kpi-form");
      } else {
        toast.error(
          "Failed to create KPI form: " + (response.message || "Unknown error")
        );
      }
    } catch (error) {
      toast.error("Failed to create KPI form: " + (error as any).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewQuestions = getPreviewQuestions();

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <CardTitle>Create KPI Form</CardTitle>
          <CardDescription>
            Select multiple question lists and order them
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/view-all-kpi-form")}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting || selectedQuestionLists.length === 0
                }
                className="flex-1"
                variant="default"
              >
                {isSubmitting ? "Creating..." : "Create KPI Form"}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Form Title</Label>
              <Input
                id="title"
                placeholder="e.g., Q1 2024 Performance Review"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                placeholder="Brief description of this form"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="division">Division</Label>
              <Select value={division} onValueChange={setDivision}>
                <SelectTrigger>
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  {divisions.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Question Lists</Label>
              <div className="border rounded-md p-4 space-y-2 max-h-60 overflow-y-auto">
                {isLoadingLists ? (
                  <p className="text-sm text-muted-foreground">
                    Loading question lists...
                  </p>
                ) : questionLists.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No question lists available
                  </p>
                ) : (
                  questionLists.map((list) => {
                    const isSelected = selectedQuestionLists.some(
                      (sql) => sql.questionListId === list._id
                    );
                    return (
                      <div
                        key={list._id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                          isSelected ? "bg-primary/10" : "bg-muted"
                        }`}
                        onClick={() => handleQuestionListToggle(list._id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleQuestionListToggle(list._id)}
                          className="cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{list.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {list.questions?.length || 0} questions
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {selectedQuestionLists.length > 0 && (
              <div className="space-y-2">
                <Label>Question List Order</Label>
                <div className="border rounded-md p-4 space-y-2">
                  {selectedQuestionLists.map((selectedList, index) => (
                    <div
                      key={selectedList.questionListId}
                      className="flex items-center gap-2 p-2 bg-muted rounded"
                    >
                      <span className="font-mono text-sm text-muted-foreground w-8">
                        {index + 1}.
                      </span>
                      <span className="flex-1">{selectedList.title}</span>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => moveQuestionListUp(index)}
                          disabled={index === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => moveQuestionListDown(index)}
                          disabled={index === selectedQuestionLists.length - 1}
                        >
                          ↓
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Use ↑ ↓ buttons to reorder question lists
                </p>
              </div>
            )}

            {previewQuestions.length > 0 && (
              <div className="space-y-2">
                <Label>Questions Preview ({previewQuestions.length} total)</Label>
                <div className="border rounded-md p-4 space-y-2 max-h-96 overflow-y-auto">
                  {previewQuestions.map((q, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-2 bg-muted rounded text-sm"
                    >
                      <span className="font-mono text-muted-foreground w-8">
                        {index + 1}.
                      </span>
                      <div className="flex-1">
                        <div>{q.text}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          From: {q.questionListTitle}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Questions cannot be reordered individually. They follow their
                  question list order.
                </p>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
